<script lang="tsx">
  import { readonly, useId, type PropType } from "vue";
  import { Button, FloatLabel, Message, Password } from "primevue";
  import PasswordStrengthSuspend from "./PasswordStrengthSuspend.vue";
  import { Form, type FormResolverOptions, type FormSubmitEvent } from "@primevue/forms";
  import { useUrlSearchParams } from "@vueuse/core";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowRight, faCircleNotch } from "@fortawesome/free-solid-svg-icons";


  export default {
    components: {
      Form, Button, FloatLabel, Password, Icon, Message,
      PasswordStrengthSuspend
    },
    setup() {
      return {
        id: useId(),
        query: readonly(useUrlSearchParams("history"))
      };
    },
    props: {
      site: {
        type: String as PropType<"enca" | "frca">,
        required: true,
      }
    },
    data() { return {
      faArrowRight, faCircleNotch,
      submitting: false,
      passwordScore: 4,
      serverError: undefined as string | undefined,
      successMessage: undefined as string | undefined,
    }},
    computed: {
      accountUid() {
        return Array.isArray(this.query.id) ? this.query.id[0] : this.query.id;
      },
      code() {
        return Array.isArray(this.query.code) ? this.query.code[0] : this.query.code;
      },
    },
    methods: {
      resolve({ values }: FormResolverOptions): Record<string, any> {
        const errors: Record<string, any[]> = { password: [] };

        if (values.password == null || values.password === "") {
          errors.password.push({ message: "New password is required" });
        }

        if (this.passwordScore <= 1) {
          errors.password.push({
            message: `New password is a very weak password`
          });
        } else if (this.passwordScore === 2) {
          errors.password.push({
            message: `New password is a weak password`
          });
        }

        return { errors };
      },

      async submit(submit: FormSubmitEvent) {
        if (!submit.valid) { return; }
        try {
          this.submitting = true;
          const result = await this.$session.resetPassword(
            this.accountUid,
            this.code,
            submit.states.password.value
          );
          if (result.errorCode !== undefined) {
            this.serverError = result.message;
          } else if (result.redirect !== undefined) {
            this.serverError = undefined;
            location.href = result.redirect;
          } else {
            this.successMessage = result.message ?? "Password successfully changed.";
          }
        } finally {
          this.submitting = false;
        }
      },
    },
  };
</script>

<template>
  <Form v-slot="form" class="form" :initial-values="{username: null, password: null}"
    validate-on-value-update validate-on-submit validate-on-blur
    :resolver="resolve" @submit="submit">
    <h3>Change Password</h3>

    <div v-if="successMessage === undefined" style="display: contents">
      <FloatLabel variant="on">
        <Password :id="`${id}-password`" name="password"
          fluid required toggleMask feedback>
          <template #content>
            <PasswordStrengthSuspend lang="en" :password="form!.password!.value"
              @update:score="passwordScore = $event"/>
          </template>
        </Password>
        <label :for="`${id}-username`">
          New Password<!--
          --><span class="required-star" aria-label="Required field" title="Required field"></span>
        </label>
      </FloatLabel>

      <Message v-if="(form.password?.errors?.length ?? 0) > 0" severity="error" size="small" variant="simple">
        {{ form.password.error.message }}
      </Message>

      <Message v-if="serverError !== undefined"
        severity="error" size="small" variant="simple">
        {{ serverError }}
      </Message>

      <Button type="submit" label="Change Password"
        formnovalidate :disabled="!form.valid || submitting"
        icon-pos="right">
        <template #icon="slot">
          <Icon :class="['fa', slot.class]" :icon="submitting ? faCircleNotch : faArrowRight" :spin="submitting"/>
        </template>
      </Button>
    </div>

    <div v-else style="display: contents">
      <span class="success">{{ successMessage }}</span>

      <div class="extra-links">
        <a href="/en-ca/account/login">Back to login</a>
      </div>
    </div>
  </Form>
</template>

<style lang="less" scoped>
  h3 { text-align: center; }

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-inline);
    justify-content: center;
    align-content: center;

    max-width: 35em;
    margin: 0 auto;
  }

  .extra-links {
    display: inline-flex;
    flex-direction: row;
    * {
      flex: 1 0;
      text-align: center;
    }
  }

  .required-star::before {
    content: "*";
    color: var(--p-floatlabel-invalid-color);
  }

  .success { text-align: center; }
</style>
